import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid, Typography, Pagination, Stack } from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import CustomerCard from "../components/customers/CustomerCard";
import CreateCustomerModal from "../components/customers/CreateCustomerModal";
import UpdateCustomerModal from "../components/customers/UpdateCustomerModal";
import ViewCustomerModal from "../components/customers/ViewCustomerModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getCustomers,
  getCustomerById,
  deleteCustomer,
} from "../redux/actions/customerActions";
import { customerActions } from "../redux/slices/customer";
import PageError from "../components/common/PageError";

const CustomersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: customers,
    pagination,
    loadingFetchAll: loadingCustomers,
    errorFetchAll: errorLoadingCustomers,
    serverStatus,
    loadingDelete: loadingCustomerDelete,
    loadingUpdate: loadingCustomerUpdate,
    loadingCreate: loadingCustomerCreate,
    item: customer,
    loadingFetchOne: loadingCustomer,
    errorFetchOne: errorLoadingCustomer,
    errorCreate: errorCreatingCustomer,
    errorUpdate: errorUpdatingCustomer,
  } = useAppSelector((state) => state.customer);

  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(12);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const canCreate = hasPermission("customer:create");

  const fetchCustomers = useCallback(() => {
    dispatch(getCustomers({ page, size: limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      setIsCreateModalOpen(false);
    if (operation === "update" && status === 200) setIsUpdateModalOpen(false);
    if (operation === "delete" && status === 200) setIsDeleteModalOpen(false);
    dispatch(customerActions.setServerStatus(null));
  }, [serverStatus, dispatch]);

  const renderContent = (): React.JSX.Element => {
    if (loadingCustomers) return <AppCircularProgress />;
    if (errorLoadingCustomers)
      return (
        <PageError error={errorLoadingCustomers} onRetry={fetchCustomers} />
      );
    if (!customers || customers.length === 0) {
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography>No customers found.</Typography>
        </Box>
      );
    }

    return (
      <>
        <Grid container spacing={3}>
          {customers.map((c) => (
            <Grid key={c.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <CustomerCard
                customer={c}
                onView={() => {
                  dispatch(getCustomerById(c.id));
                  setIsViewModalOpen(true);
                }}
                onEdit={() => {
                  dispatch(getCustomerById(c.id));
                  setIsUpdateModalOpen(true);
                }}
                onDelete={() => {
                  setSelectedId(c.id);
                  setIsDeleteModalOpen(true);
                }}
              />
            </Grid>
          ))}
        </Grid>
        {pagination && pagination.totalPages! > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_e, v) => setPage(v)}
              color="primary"
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Customer Directory"
      subtitle="Manage business accounts, credit limits, and loyalty programs."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Register Customer
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by name, contact or email..."
      />
      {renderContent()}

      {isCreateModalOpen && (
        <CreateCustomerModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          loading={loadingCustomerCreate}
          error={errorCreatingCustomer}
        />
      )}
      {isUpdateModalOpen && customer && (
        <UpdateCustomerModal
          open={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          customer={customer}
          loading={loadingCustomerUpdate || loadingCustomer}
          error={errorUpdatingCustomer}
        />
      )}
      {isViewModalOpen && (
        <ViewCustomerModal
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          customer={customer}
          loading={loadingCustomer}
          error={errorLoadingCustomer}
        />
      )}
      {isDeleteModalOpen && selectedId && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={() => dispatch(deleteCustomer(selectedId))}
          title="Delete Customer"
          description="Permanently remove this customer? Historical sales records will be preserved but the profile will be deleted."
          loading={loadingCustomerDelete}
        />
      )}
    </PageContainer>
  );
};

export default CustomersPage;
